/**
 * Application_Form — shared Zod derivation (application-form-builder spec, task 20.1).
 *
 * Builds the client-side Zod schema an applicant's Form_Renderer (React Hook
 * Form + Zod) validates against, derived from the active `FormVersion`
 * snapshot. The decisive design constraint is **validation parity** (Req 5 ↔
 * Req 7, Property 20): the client and the server must accept exactly the same
 * responses. We guarantee that by *not re-implementing any validation rule
 * here* — instead this module imports and delegates to the very same pure
 * module the Lambda runs at submit time:
 *
 *   `infrastructure/lambda/application-form/fieldValidation.ts`
 *
 * The frontend reaches up into the infrastructure package directly (a relative
 * cross-import) so client and server validation are *provably the same code*,
 * exactly the "pure core, shared with the frontend" intent the design records:
 * "the same `fieldValidation.ts` the server runs is the basis for the
 * client-side Zod schema, which is how validation parity is guaranteed rather
 * than re-implemented twice." `fieldValidation.ts` is I/O-free (it only pulls
 * in the pure `FieldType` enum from `infrastructure/types/database.ts`), so it
 * bundles cleanly into the Vite/Vitest build.
 *
 * How the schema is built:
 *   - The object shape has one optional key per **CUSTOM** field in the
 *     snapshot. **BOUND** fields render a control but store no value in the
 *     response, so they are excluded from the schema entirely (Req 17.2, 17.6) —
 *     and `.passthrough()` keeps any stray key alive long enough for
 *     `validateResponse` to flag it as `BOUND_VALUE_NOT_ALLOWED`, preserving
 *     parity with the server.
 *   - Each value is accepted structurally as-is (`z.custom`); EVERY semantic
 *     decision — type/options/limit/format checks, per-division visibility, and
 *     required-completeness — is delegated to `validateResponse` in a single
 *     top-level `.superRefine`. Because the only way the schema can fail is a
 *     `validateResponse` error, the schema accepts a response iff
 *     `validateResponse` accepts it (Property 20).
 *
 * The `enforceRequired` option mirrors the server's two modes: `true` for
 * submit (required-and-visible fields must be answered, Req 5.3/7.3) and
 * `false` for save-as-draft (no required enforcement, Req 6.1). Property 20
 * pairs the client schema with `validateResponse` run under the *same*
 * `enforceRequired` setting.
 *
 * Validates: Requirements 5.1, 5.2, 5.3, 5.4, 5.5, 5.6
 */

import { z } from 'zod';
import {
  validateResponse,
  type FormVersionSnapshot,
  type ResponseValue,
} from '../../../../infrastructure/lambda/application-form/fieldValidation';

/** The applicant-entered values an Application_Response stores: CUSTOM field id → value. */
export type ApplicationResponseValues = Record<string, ResponseValue>;

export interface DeriveZodSchemaOptions {
  /**
   * Whether required-and-visible CUSTOM fields must be answered. `true` for
   * submit (Req 5.3, 7.3); `false` for save-as-draft (Req 6.1). Defaults to
   * `true` (the submit gate). Mirrors the server's `validateResponse` option
   * so the two paths can be compared under identical settings (Property 20).
   */
  enforceRequired?: boolean;
  /**
   * Whether the applicant's Phase 2 is unlocked
   * (`isPhaseTwoUnlocked(submissionStatus)`, i.e. `submissionStatus ===
   * APPROVED`). Threaded straight through to the server's `validateResponse`
   * so the client and server submit gates enforce required-completeness for a
   * `PHASE_2` field under the SAME phase-aware rule
   * (application-builder-authoring-fixes, Defect A). The applicant submit path
   * derives it from the response's `submissionStatus`. Defaults to "enforce all
   * phases" (`undefined` ⇒ the server's `?? true`) when omitted.
   */
  phaseTwoUnlocked?: boolean;
}

/**
 * A deliberately lenient per-value schema. It accepts any structural value and
 * defers EVERY content decision to `fieldValidation` (via `validateResponse`).
 * This is what keeps the schema parity-exact: the schema never rejects a value
 * that `validateValue` would accept, because it never inspects the value itself.
 */
const responseValueSchema = z.custom<ResponseValue>(() => true);

/** The ids of every CUSTOM field across the snapshot's pages (BOUND fields excluded). */
function customFieldIds(snapshot: FormVersionSnapshot): string[] {
  const ids: string[] = [];
  for (const page of snapshot.pages) {
    for (const field of page.fields) {
      if (field.fieldCategory === 'CUSTOM') {
        ids.push(field.id);
      }
    }
  }
  return ids;
}

/**
 * Derive the client Zod schema for an applicant completing `snapshot` while
 * assigned to `divisionKey`.
 *
 * The returned schema validates an `ApplicationResponseValues` map. It accepts
 * the map iff the server's `validateResponse(snapshot, values, divisionKey,
 * { enforceRequired })` reports no errors — the two share `fieldValidation.ts`,
 * so they never diverge (Property 20). When the schema rejects, each
 * `FieldValidationError` is surfaced as a Zod issue whose `path` is the
 * offending field id (so React Hook Form maps the message to the right control)
 * and whose `params.fieldValidationCode` carries the machine-readable code.
 */
export function deriveZodSchema(
  snapshot: FormVersionSnapshot,
  divisionKey: string,
  opts: DeriveZodSchemaOptions = {},
): z.ZodType<ApplicationResponseValues> {
  const enforceRequired = opts.enforceRequired ?? true;
  const phaseTwoUnlocked = opts.phaseTwoUnlocked;

  // One optional key per CUSTOM field; BOUND fields contribute no stored value
  // and are omitted from the shape (Req 17.2, 17.6).
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const fieldId of customFieldIds(snapshot)) {
    shape[fieldId] = responseValueSchema.optional();
  }

  const schema = z
    .object(shape)
    // Keep stray keys (e.g. a value mistakenly supplied for a BOUND field) so
    // they reach `validateResponse`, which flags them BOUND_VALUE_NOT_ALLOWED —
    // matching the server and preserving parity.
    .passthrough()
    .superRefine((values, ctx) => {
      const errors = validateResponse(
        snapshot,
        values as ApplicationResponseValues,
        divisionKey,
        { enforceRequired, phaseTwoUnlocked },
      );
      for (const error of errors) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [error.fieldId],
          message: error.message,
          params: { fieldValidationCode: error.code },
        });
      }
    });

  return schema as unknown as z.ZodType<ApplicationResponseValues>;
}
